/**
 * `clawnify skills` — install / list / remove Clawnify-curated skills
 * into whichever coding agents you have in the current directory.
 *
 * Behavior:
 *   - `list` shows every skill the CLI bundles + its version.
 *   - `add <name|--all>` writes the skill into EVERY detected agent
 *     simultaneously (.claude, .cursor, .openclaw, .codex, aider).
 *   - `remove <name|--all>` cleans them back out of every agent.
 *
 * Version semantics: each installed file carries a
 * `<!-- clawnify-skill: <name> v<X.Y.Z> -->` header. Re-running `add`
 * on the same CLI version is a no-op; upgrading the CLI (`npm install
 * -g clawnify@latest`) then re-running `add` overwrites with the new
 * content. This is the only way skills update — there is no runtime
 * network fetch.
 */

import { SKILLS, CLI_VERSION, type BundledSkill } from "../lib/skills/registry.js";
import {
  agentByName,
  detectAgents,
  ALL_AGENTS,
  type Agent,
  type DetectedAgent,
} from "../lib/skills/detect.js";
import { installSkill, removeSkill } from "../lib/skills/install.js";

interface AddOptions {
  all?: boolean;
  agent?: string;
}

interface RemoveOptions {
  all?: boolean;
  agent?: string;
}

export function skillsListCommand(): void {
  console.log(`Clawnify skills (v${CLI_VERSION}):\n`);
  for (const s of SKILLS) {
    console.log(`  ${s.name}`);
    console.log(`    ${s.description}\n`);
  }
  console.log(`Add one:    clawnify skills add <name>`);
  console.log(`Add all:    clawnify skills add --all`);
  console.log(`Remove:     clawnify skills remove <name>`);
}

export async function skillsAddCommand(name: string | undefined, opts: AddOptions): Promise<void> {
  const skills = resolveSkills(name, opts.all);
  if (skills.length === 0) {
    console.error("No skill specified. Pass a name or --all. See `clawnify skills list`.");
    process.exit(1);
  }

  const agents = resolveAgents(opts.agent);
  if (agents.length === 0) {
    console.error(
      "No coding agent detected in this directory. Looked for:\n" +
        "  .claude/  (Claude Code)\n" +
        "  .cursor/  (Cursor)\n" +
        "  .openclaw/ (OpenClaw)\n" +
        "  .codex/ or AGENTS.md (Codex)\n" +
        "  CONVENTIONS.md or .aider.conf.yml (Aider)\n\n" +
        "Force a target with --agent <claude|cursor|openclaw|codex|aider>.",
    );
    process.exit(1);
  }

  const cwd = process.cwd();
  console.log(
    `Installing ${skills.length} skill${skills.length === 1 ? "" : "s"} into ${agents.length} agent${agents.length === 1 ? "" : "s"}:`,
  );
  console.log(`  CLI version: v${CLI_VERSION}`);
  console.log(
    `  Agents:      ${agents.map((a) => `${a.agent} (${a.marker})`).join(", ")}\n`,
  );

  let installed = 0;
  let updated = 0;
  let skipped = 0;
  for (const skill of skills) {
    for (const agent of agents) {
      const result = installSkill(skill, agent, cwd);
      if (result.status === "installed") {
        installed++;
        console.log(`  ✓ ${skill.name} → ${result.path}`);
      } else if (result.status === "updated") {
        updated++;
        console.log(
          `  ↑ ${skill.name} → ${result.path}  (v${result.previousVersion} → v${CLI_VERSION})`,
        );
      } else {
        skipped++;
        console.log(`  · ${skill.name} → ${result.path}  (${result.reason})`);
      }
    }
  }
  console.log(
    `\nDone — ${installed} installed, ${updated} updated, ${skipped} unchanged.`,
  );
}

export async function skillsRemoveCommand(name: string | undefined, opts: RemoveOptions): Promise<void> {
  const skills = resolveSkills(name, opts.all);
  if (skills.length === 0) {
    console.error("No skill specified. Pass a name or --all.");
    process.exit(1);
  }

  const agents = resolveAgents(opts.agent);
  if (agents.length === 0) {
    console.error("No coding agent detected. Nothing to remove.");
    return;
  }

  const cwd = process.cwd();
  let removed = 0;
  for (const skill of skills) {
    for (const agent of agents) {
      if (removeSkill(skill.name, agent, cwd)) {
        removed++;
        console.log(`  ✗ ${skill.name} removed from ${agent.agent}`);
      }
    }
  }
  console.log(`\nDone — removed ${removed} file${removed === 1 ? "" : "s"}.`);
}

// ── Helpers ───────────────────────────────────────────────────────────

function resolveSkills(name: string | undefined, all: boolean | undefined): BundledSkill[] {
  if (all) return [...SKILLS];
  if (!name) return [];
  const match = SKILLS.find((s) => s.name === name);
  if (!match) {
    console.error(
      `Unknown skill: ${name}\nAvailable: ${SKILLS.map((s) => s.name).join(", ")}`,
    );
    process.exit(1);
  }
  return [match];
}

function resolveAgents(forced: string | undefined): DetectedAgent[] {
  if (forced) {
    if (!ALL_AGENTS.includes(forced as Agent)) {
      console.error(
        `Unknown agent: ${forced}\nValid: ${ALL_AGENTS.join(", ")}`,
      );
      process.exit(1);
    }
    return [agentByName(forced as Agent)];
  }
  return detectAgents(process.cwd());
}
